import {
  ArrowRight,
  ChartBar,
  ClipboardList,
  Coins,
  FileClock,
  HandCoins,
  Receipt,
  Timer,
  Wallet,
} from 'lucide-react';

import { BarChart } from '@/components/charts/bar-chart';
import { DonutChart } from '@/components/charts/donut-chart';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { cn, formatCurrencyAdaptive, formatNumber, toNumber } from '@/lib/utils';

import type { DocumentacionEstado, ResumenAutoservicio } from '../autoservicio.types';
import {
  ESTADOS_DOCUMENTACION,
  ESTADOS_OUTBOX,
  ESTADOS_SOLICITUD,
  mensajeAutoservicio,
  varianteOutbox,
} from '../autoservicio-format';
import { KpiAutoservicio } from './kpi-autoservicio';

interface PanelResumenProps {
  datos: ResumenAutoservicio | undefined;
  cargando: boolean;
  error: unknown;
  onVerTurnos: () => void;
  onVerOperaciones: () => void;
  onVerCola: () => void;
}

/** Fila vacía de documentación (estado sin operaciones en el período). */
const DOCUMENTACION_VACIA: DocumentacionEstado = {
  cantidad: 0,
  montoPresupuestado: 0,
  montoCobrado: 0,
  saldoAplicado: 0,
  conCodigoSaldo: 0,
};

const TONOS_INDICADOR = {
  brand: 'bg-brand-500',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  rose: 'bg-rose-500',
} as const;

/** Porcentaje acotado a 0..100 para una barra. */
function acotar(porcentaje: number): number {
  if (!Number.isFinite(porcentaje)) return 0;

  return Math.max(0, Math.min(100, porcentaje));
}

/** Indicador con barra proporcional (tasas del período). */
function Indicador({
  etiqueta,
  porcentaje,
  detalle,
  tono = 'brand',
}: {
  etiqueta: string;
  porcentaje: number;
  detalle: string;
  tono?: keyof typeof TONOS_INDICADOR;
}) {
  const valor = acotar(porcentaje);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-ink-700">{etiqueta}</span>
        <span className="font-mono text-sm font-semibold text-ink-900 tabular-nums">
          {Number.isFinite(porcentaje) ? `${porcentaje.toFixed(1)} %` : '—'}
        </span>
      </div>
      <div
        className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink-100"
        role="img"
        aria-label={`${etiqueta}: ${porcentaje.toFixed(1)} %`}
      >
        <span
          className={cn('block h-full rounded-full', TONOS_INDICADOR[tono])}
          style={{ width: `${valor}%` }}
        />
      </div>
      <p className="mt-1 text-[11px] text-ink-400">{detalle}</p>
    </div>
  );
}

/**
 * Pantalla **Dashboard** del canal de autoservicio.
 *
 * KPIs del período, distribución de solicitudes, comprobantes, salud de la cola,
 * comparación presupuesto/cobrado por estado, indicadores derivados y el
 * desglose fiscal completo (con fila de totales).
 */
export function PanelResumen({
  datos,
  cargando,
  error,
  onVerTurnos,
  onVerOperaciones,
  onVerCola,
}: PanelResumenProps) {
  if (error) {
    return (
      <Alert variant="error" title="No se pudo cargar el resumen">
        {mensajeAutoservicio(error)}
      </Alert>
    );
  }

  const solicitudes = datos?.solicitudes;
  const documentacion = datos?.documentacion;
  const comprobantes = datos?.comprobantes;
  const montos = datos?.montos;
  const outbox = datos?.outbox;

  const totalSolicitudes = toNumber(solicitudes?.total);
  const documentadas = toNumber(documentacion?.total);
  const conCodigoSaldo = toNumber(documentacion?.solicitudesConCodigoSaldo);
  const cobrado = toNumber(montos?.cobrado);
  const presupuestado = toNumber(montos?.presupuestado);
  const saldoAplicado = toNumber(montos?.saldoAplicado);

  const boletas = comprobantes?.boletas;
  const facturas = comprobantes?.facturas;
  const otros = comprobantes?.otros;

  const filasComprobantes = [
    { etiqueta: 'Boletas', cantidad: toNumber(boletas?.cantidad), monto: toNumber(boletas?.monto) },
    { etiqueta: 'Facturas', cantidad: toNumber(facturas?.cantidad), monto: toNumber(facturas?.monto) },
    { etiqueta: 'Otros', cantidad: toNumber(otros?.cantidad), monto: toNumber(otros?.monto) },
  ];

  const totalComprobantes = filasComprobantes.reduce((suma, fila) => suma + fila.cantidad, 0);
  const montoComprobantes = filasComprobantes.reduce((suma, fila) => suma + fila.monto, 0);

  const porEstado = documentacion?.porEstado ?? {};
  const filasDocumentacion = ESTADOS_DOCUMENTACION.map((estado) => ({
    ...estado,
    datos: porEstado[estado.clave] ?? DOCUMENTACION_VACIA,
  }));

  const totalesDocumentacion = filasDocumentacion.reduce(
    (totales, fila) => ({
      cantidad: totales.cantidad + fila.datos.cantidad,
      montoPresupuestado: totales.montoPresupuestado + toNumber(fila.datos.montoPresupuestado),
      montoCobrado: totales.montoCobrado + toNumber(fila.datos.montoCobrado),
      saldoAplicado: totales.saldoAplicado + toNumber(fila.datos.saldoAplicado),
      conCodigoSaldo: totales.conCodigoSaldo + toNumber(fila.datos.conCodigoSaldo),
    }),
    { cantidad: 0, montoPresupuestado: 0, montoCobrado: 0, saldoAplicado: 0, conCodigoSaldo: 0 },
  );

  const tasaDocumentacion = totalSolicitudes > 0 ? (documentadas / totalSolicitudes) * 100 : 0;
  const coberturaCobro = presupuestado > 0 ? (cobrado / presupuestado) * 100 : 0;
  const usoCodigo = documentadas > 0 ? (conCodigoSaldo / documentadas) * 100 : 0;
  const ticketPromedio = documentadas > 0 ? cobrado / documentadas : 0;

  const totalOutbox = ESTADOS_OUTBOX.reduce(
    (suma, estado) => suma + toNumber(outbox?.porEstado[estado.clave]),
    0,
  );

  return (
    <div className="space-y-4">
      {/* Indicadores del período */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiAutoservicio
          label="Solicitudes"
          value={formatNumber(totalSolicitudes)}
          hint={`${formatNumber(solicitudes?.porEstado.completed)} completadas · ${formatNumber(
            solicitudes?.porEstado.cancelled,
          )} canceladas · ${formatNumber(solicitudes?.porEstado.error)} con error`}
          icon={ClipboardList}
          tono="brand"
          cargando={cargando && !datos}
        />

        <KpiAutoservicio
          label="Documentadas"
          value={formatNumber(documentadas)}
          hint={`${tasaDocumentacion.toFixed(1)} % de las solicitudes`}
          icon={Receipt}
          tono="emerald"
          cargando={cargando && !datos}
        />

        <KpiAutoservicio
          label="Monto cobrado"
          value={formatCurrencyAdaptive(cobrado)}
          hint={`${coberturaCobro.toFixed(1)} % de ${formatCurrencyAdaptive(presupuestado)}`}
          icon={Wallet}
          tono="accent"
          cargando={cargando && !datos}
        />

        <KpiAutoservicio
          label="Saldo aplicado"
          value={formatCurrencyAdaptive(saldoAplicado)}
          hint={`${formatNumber(conCodigoSaldo)} operaciones con código`}
          icon={HandCoins}
          tono="amber"
          cargando={cargando && !datos}
        />
      </div>

      {/* Distribución: solicitudes, comprobantes y cola */}
      <div className="grid items-stretch gap-4 lg:grid-cols-3">
        <SectionCard
          title="Solicitudes por estado"
          description="Autorizaciones de bomba del período"
        >
          <DonutChart
            labels={ESTADOS_SOLICITUD.map((estado) => estado.etiqueta)}
            series={[
              {
                label: 'Solicitudes',
                data: ESTADOS_SOLICITUD.map((estado) =>
                  toNumber(solicitudes?.porEstado[estado.clave]),
                ),
              },
            ]}
            format="number"
          />

          <p className="mt-3 border-t border-ink-100 pt-3 text-[11px] text-ink-400">
            «En proceso» es una autorización en curso; «Con error» requiere revisión.
          </p>
        </SectionCard>

        <SectionCard title="Comprobantes" description="Monto emitido por tipo de documento">
          <DonutChart
            labels={filasComprobantes.map((fila) => fila.etiqueta)}
            series={[
              {
                label: 'Monto',
                data: filasComprobantes.map((fila) => fila.monto),
              },
            ]}
            format="currency"
          />

          <div className="mt-3 flex items-center justify-between border-t border-ink-100 pt-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-ink-400">Emitidos</p>
              <p className="font-mono text-lg font-bold text-ink-900 tabular-nums">
                {formatNumber(totalComprobantes)}
              </p>
            </div>

            <div className="text-right">
              <p className="text-[11px] uppercase tracking-wider text-ink-400">Monto</p>
              <p
                className="truncate font-mono text-sm font-semibold text-ink-900 tabular-nums"
                title={formatCurrencyAdaptive(montoComprobantes)}
              >
                {formatCurrencyAdaptive(montoComprobantes)}
              </p>
            </div>
          </div>

          <p className="mt-2 text-[11px] text-ink-400">
            «Otros» son documentados sin boleta ni factura.
          </p>
        </SectionCard>

        <SectionCard title="Salud de la cola" description="Envío de comprobantes al sistema fiscal">
          <DonutChart
            labels={ESTADOS_OUTBOX.map((estado) => estado.etiqueta)}
            series={[
              {
                label: 'Envíos',
                data: ESTADOS_OUTBOX.map((estado) => toNumber(outbox?.porEstado[estado.clave])),
              },
            ]}
            format="number"
          />

          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ink-100 pt-3">
            <Badge variant={varianteOutbox('pending')}>
              {formatNumber(outbox?.porEstado.pending)} pendientes
            </Badge>
            <Badge variant={varianteOutbox('failed')}>
              {formatNumber(outbox?.porEstado.failed)} fallidos
            </Badge>
            <span className="text-[11px] text-ink-400">
              {formatNumber(totalOutbox)} envíos · reintentos {formatNumber(outbox?.reintentos.total)} (máx.{' '}
              {formatNumber(outbox?.reintentos.maximo)})
            </span>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={onVerCola}
            rightIcon={<ArrowRight className="size-3.5" aria-hidden="true" />}
          >
            Ver cola de envío
          </Button>
        </SectionCard>
      </div>

      {/* Comparación económica + indicadores derivados */}
      <div className="grid items-stretch gap-4 xl:grid-cols-3">
        <SectionCard
          className="xl:col-span-2"
          title="Composición del presupuesto por estado"
          description="Lo cobrado y lo que queda por cobrar en cada estado de documentación"
          action={<ChartBar className="size-4 shrink-0 text-ink-400" aria-hidden="true" />}
        >
          <BarChart
            labels={filasDocumentacion.map((fila) => fila.etiqueta)}
            series={[
              {
                label: 'Cobrado',
                data: filasDocumentacion.map((fila) => toNumber(fila.datos.montoCobrado)),
              },
              {
                label: 'Por cobrar',
                data: filasDocumentacion.map((fila) =>
                  Math.max(
                    toNumber(fila.datos.montoPresupuestado) - toNumber(fila.datos.montoCobrado),
                    0,
                  ),
                ),
              },
            ]}
            format="currency"
            stacked
            height={300}
          />

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-ink-100 pt-3 text-sm">
            <span className="text-ink-500">
              Presupuesto total{' '}
              <span className="font-mono font-semibold text-ink-900 tabular-nums">
                {formatCurrencyAdaptive(totalesDocumentacion.montoPresupuestado)}
              </span>
            </span>
            <span className="text-ink-500">
              Saldo aplicado{' '}
              <span className="font-mono font-semibold text-ink-900 tabular-nums">
                {formatCurrencyAdaptive(totalesDocumentacion.saldoAplicado)}
              </span>
            </span>
          </div>
        </SectionCard>

        <SectionCard
          title="Indicadores del período"
          description="Tasas calculadas sobre los agregados"
          action={<Timer className="size-4 shrink-0 text-ink-400" aria-hidden="true" />}
        >
          <div className="space-y-4">
            <Indicador
              etiqueta="Tasa de documentación"
              porcentaje={tasaDocumentacion}
              detalle={`${formatNumber(documentadas)} de ${formatNumber(totalSolicitudes)} solicitudes`}
              tono="brand"
            />
            <Indicador
              etiqueta="Cobertura de cobro"
              porcentaje={coberturaCobro}
              detalle={`${formatCurrencyAdaptive(cobrado)} de ${formatCurrencyAdaptive(presupuestado)}`}
              tono="emerald"
            />
            <Indicador
              etiqueta="Uso de código de saldo"
              porcentaje={usoCodigo}
              detalle={`${formatNumber(conCodigoSaldo)} operaciones con código`}
              tono="amber"
            />

            <div className="flex items-center justify-between gap-3 border-t border-ink-100 pt-3">
              <span className="flex items-center gap-2 text-sm text-ink-700">
                <Coins className="size-4 text-ink-400" aria-hidden="true" />
                Ticket promedio
              </span>
              <span
                className="truncate font-mono text-sm font-semibold text-ink-900 tabular-nums"
                title={formatCurrencyAdaptive(ticketPromedio)}
              >
                {formatCurrencyAdaptive(ticketPromedio)}
              </span>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Accesos a las otras pantallas del módulo */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onVerTurnos}
          rightIcon={<ArrowRight className="size-3.5" aria-hidden="true" />}
        >
          Operación por turno
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onVerOperaciones}
          rightIcon={<ArrowRight className="size-3.5" aria-hidden="true" />}
        >
          Detalle de operaciones
        </Button>
      </div>

      {/* Documentación fiscal por estado */}
      <SectionCard
        title="Documentación por estado"
        description="«Pendiente de documento» también incluye las canceladas por expiración"
        action={<FileClock className="size-4 shrink-0 text-ink-400" aria-hidden="true" />}
      >
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                <th className="w-[26%] pb-2 pr-2">Estado</th>
                <th className="w-[10%] pb-2 pr-2 text-right">Cant.</th>
                <th className="w-[17%] pb-2 pr-2 text-right">Presupuestado</th>
                <th className="w-[17%] pb-2 pr-2 text-right">Cobrado</th>
                <th className="w-[17%] pb-2 pr-2 text-right">Saldo aplicado</th>
                <th className="w-[13%] pb-2 text-right">C/ código</th>
              </tr>
            </thead>

            <tbody>
              {filasDocumentacion.map((fila) => (
                <tr key={fila.clave} className="border-b border-ink-50 last:border-0">
                  <td className="py-2.5 pr-2">
                    <Badge variant={fila.variante}>{fila.etiqueta}</Badge>
                  </td>
                  <td className="py-2.5 pr-2 text-right font-mono text-ink-900 tabular-nums">
                    {formatNumber(fila.datos.cantidad)}
                  </td>
                  <td
                    className="truncate py-2.5 pr-2 text-right font-mono text-ink-700 tabular-nums"
                    title={formatCurrencyAdaptive(fila.datos.montoPresupuestado)}
                  >
                    {formatCurrencyAdaptive(fila.datos.montoPresupuestado)}
                  </td>
                  <td
                    className="truncate py-2.5 pr-2 text-right font-mono text-ink-700 tabular-nums"
                    title={formatCurrencyAdaptive(fila.datos.montoCobrado)}
                  >
                    {formatCurrencyAdaptive(fila.datos.montoCobrado)}
                  </td>
                  <td
                    className="truncate py-2.5 pr-2 text-right font-mono text-ink-700 tabular-nums"
                    title={formatCurrencyAdaptive(fila.datos.saldoAplicado)}
                  >
                    {formatCurrencyAdaptive(fila.datos.saldoAplicado)}
                  </td>
                  <td className="py-2.5 text-right font-mono text-ink-700 tabular-nums">
                    {formatNumber(fila.datos.conCodigoSaldo)}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr className="border-t border-ink-200 bg-ink-50/60 text-ink-900">
                <td className="py-2.5 pr-2 pl-1 text-xs font-semibold uppercase tracking-wider">Total</td>
                <td className="py-2.5 pr-2 text-right font-mono font-semibold tabular-nums">
                  {formatNumber(totalesDocumentacion.cantidad)}
                </td>
                <td
                  className="truncate py-2.5 pr-2 text-right font-mono font-semibold tabular-nums"
                  title={formatCurrencyAdaptive(totalesDocumentacion.montoPresupuestado)}
                >
                  {formatCurrencyAdaptive(totalesDocumentacion.montoPresupuestado)}
                </td>
                <td
                  className="truncate py-2.5 pr-2 text-right font-mono font-semibold tabular-nums"
                  title={formatCurrencyAdaptive(totalesDocumentacion.montoCobrado)}
                >
                  {formatCurrencyAdaptive(totalesDocumentacion.montoCobrado)}
                </td>
                <td
                  className="truncate py-2.5 pr-2 text-right font-mono font-semibold tabular-nums"
                  title={formatCurrencyAdaptive(totalesDocumentacion.saldoAplicado)}
                >
                  {formatCurrencyAdaptive(totalesDocumentacion.saldoAplicado)}
                </td>
                <td className="py-2.5 text-right font-mono font-semibold tabular-nums">
                  {formatNumber(totalesDocumentacion.conCodigoSaldo)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="mt-3 text-[11px] text-ink-400">
          «Sin despacho» son operaciones con despacho real 0: no se emite comprobante.
        </p>
      </SectionCard>
    </div>
  );
}
